/**
 * Order Tracking Report Component
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ReportResponse, OrderTrackingItem, OrderStatus, DeliveryStatus } from '../../types/reports';

const OrderTrackingReport: React.FC = () => {
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [selectedStore, setSelectedStore] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch stores
  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await api.getStores();
      return response.data;
    },
  });

  // Fetch report data
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ReportResponse<OrderTrackingItem>>({
    queryKey: ['orderTrackingReport', startDate, endDate, selectedStore, statusFilter],
    queryFn: async () => {
      const response = await api.getOrderTrackingReport({
        start_date: startDate,
        end_date: endDate,
        store_id: selectedStore || undefined,
        status: statusFilter || undefined,
      });
      return response.data;
    },
  });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response =
        format === 'csv'
          ? await api.exportReportCSV('order_tracking', {
              start_date: startDate,
              end_date: endDate,
              store_id: selectedStore || undefined,
              status: statusFilter || undefined,
            })
          : await api.exportReportJSON('order_tracking', {
              start_date: startDate,
              end_date: endDate,
              store_id: selectedStore || undefined,
              status: statusFilter || undefined,
            });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order_tracking_${startDate}_${endDate}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Export error - silently handled
    }
  };

  const getOrderStatusColor = (
    status: string
  ): 'error' | 'warning' | 'success' | 'default' | 'info' => {
    switch (status) {
      case OrderStatus.DELIVERED:
        return 'success';
      case OrderStatus.APPROVED:
        return 'info';
      case OrderStatus.CANCELLED:
        return 'error';
      case OrderStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getDeliveryStatusColor = (
    status: string
  ): 'error' | 'warning' | 'success' | 'default' => {
    switch (status) {
      case DeliveryStatus.OVERDUE:
        return 'error';
      case DeliveryStatus.LATE:
        return 'warning';
      case DeliveryStatus.ON_TIME:
        return 'success';
      case DeliveryStatus.EARLY:
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Store</InputLabel>
            <Select
              value={selectedStore}
              label="Store"
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores?.map((store: any) => (
                <MenuItem key={store.store_id} value={store.store_id}>
                  {store.store_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={statusFilter}
              label="Order Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="transmitted">Transmitted</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
            >
              Export
            </Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading report: {(error as Error).message}
        </Alert>
      )}

      {reportData && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(reportData.metadata.generated_at).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Orders: {reportData.metadata.record_count}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Item Status</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Order Status</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell align="right">Age (days)</TableCell>
                  <TableCell>Delivery Status</TableCell>
                  <TableCell>Supplier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No orders found for the selected period
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.data.map((item: OrderTrackingItem) => (
                    <TableRow key={item.order_id}>
                      <TableCell>
                        <Typography variant="caption">
                          {item.order_id.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(item.order_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{item.store_name}</TableCell>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {(item as any).lifecycle_stage ? (
                          <Chip
                            label={(item as any).lifecycle_stage}
                            size="small"
                            color={
                              (item as any).lifecycle_stage === 'NEW' ? 'info' :
                              (item as any).lifecycle_stage === 'GROWTH' ? 'success' :
                              (item as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                              ((item as any).lifecycle_stage === 'PHASE_OUT' || (item as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{item.quantity_ordered}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          color={getOrderStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {item.expected_delivery
                          ? new Date(item.expected_delivery).toLocaleDateString()
                          : 'TBD'}
                      </TableCell>
                      <TableCell align="right">{item.order_age_days}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.delivery_status}
                          color={getDeliveryStatusColor(item.delivery_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.supplier}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default OrderTrackingReport;
